import { action, observable } from 'mobx';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { LessonHomeworkIndexView } from '../../../types/lesson_homeworks';
import { LessonHomeworkId, SubmittedField } from '../../../../commonv3/types/lesson_homeworks';
import { LessonHomeworkApis } from '../../../apis/lessonHomeworks';
import { FieldStateV3, FormContext } from '../../../../commonv3/states/form/FormStateV3';
import { SubmitState } from '../../../../commonv3/states/SubmitState';
import { ArrayValidators, StringValidators } from '../../../../commonv3/states/form/validators';
import { LessonHomeworkSubmissionApis } from '../../../apis/lessonHomeworkSubmissions';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { V3Routes } from '../../../routes';
import { translateErrorMessage } from '../../../../commonv3/api/protocol';
import { SubmissionFieldType, SubmitFieldState } from '../../../../commonv3/types/course_works';

export class LessonHomeworkSubmissionPageState {
    @observable homeworkId = null;
    @observable homework: RemoteResourceV3<LessonHomeworkIndexView> = RemoteResourceV3.loading();
    @observable fieldStates: SubmitFieldState[] = [];
    @observable submitState = new SubmitState();

    @action.bound
    async init(homeworkId: LessonHomeworkId) {
        this.homeworkId = homeworkId;
        await this.reloadHomework();
    }

    @action.bound
    async reloadHomework() {
        const homework = await updateRemoteResourceV3(
            this.homework,
            (v) => (this.homework = v),
            () => LessonHomeworkApis.show(this.homeworkId)
        );
        if (homework) {
            this.fieldStates = homework.submission_field_definitions.map((definition) => {
                switch (definition.type) {
                    case SubmissionFieldType.SINGLE_LINE_TEXT:
                    case SubmissionFieldType.MULTI_LINE_TEXT:
                        return {
                            definition,
                            state: new FieldStateV3<string>(definition.required ? [StringValidators.required()] : []),
                        };
                    case SubmissionFieldType.GOOGLE_URL:
                        return {
                            definition,
                            state: new FieldStateV3<string>(
                                definition.required
                                    ? [StringValidators.required(), StringValidators.googleUrl()]
                                    : [StringValidators.googleUrl()]
                            ),
                        };
                    case SubmissionFieldType.FILE:
                        return {
                            definition,
                            state: new FieldStateV3<string[]>(
                                definition.required
                                    ? [ArrayValidators.atLeast(1, 'ファイルをアップロードしてください')]
                                    : []
                            ),
                        };
                }
            });
            this.fieldStates.map((state) =>
                state.state._init(`submission-field-${state.definition.uid}`, new FormContext(), null)
            );
        }
    }

    @action.bound
    async validate() {
        await Promise.all(
            this.fieldStates.map((field) => {
                field.state.clearErrors();
                return field.state.validate();
            })
        );
    }

    hasErrors() {
        return this.fieldStates.some((state) => state.state.hasErrors());
    }

    getDirtyValue(): SubmittedField[] {
        return this.fieldStates.map((field) => {
            return {
                definition: field.definition,
                value: field.state.getDirtyValue(),
            } as any;
        });
    }

    @action.bound
    async submit() {
        await this.validate();
        if (this.hasErrors()) {
            return;
        }
        try {
            await this.submitState.operate(() =>
                LessonHomeworkSubmissionApis.create(this.homeworkId, this.getDirtyValue())
            );
            GlobalStatesV3.routing.push(V3Routes.lessonHomeworkSubmissionComplete(this.homeworkId));
        } catch (e) {
            alert('提出に失敗しました : ' + translateErrorMessage(e));
        }
    }
}
